import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// A task currently opened in the bottom panel's detail pane. Volatile — not
// persisted (reopening the app shouldn't reopen a stale run).
export interface SelectedTask {
  tenantId: string;
  runId: string;
}

export interface ShellContextValue {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  taskPanelCollapsed: boolean;
  toggleTaskPanel: () => void;
  taskPanelHeight: number;
  setTaskPanelHeight: (px: number) => void;

  isTenantExpanded: (tenantId: string) => boolean;
  toggleTenant: (tenantId: string) => void;

  selectedTask: SelectedTask | null;
  openTask: (task: SelectedTask) => void;
  closeTask: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ShellContext = createContext<ShellContextValue | null>(null);

const PERSIST = {
  sidebar: "ps.shell.sidebarCollapsed",
  taskCollapsed: "ps.shell.taskCollapsed",
  taskHeight: "ps.shell.taskHeight",
  expanded: "ps.shell.expandedTenants",
} as const;

const MIN_TASK_HEIGHT = 120;
const DEFAULT_TASK_HEIGHT = 220;

function maxTaskHeight(): number {
  // 60vh ceiling so the panel never swallows the whole content area.
  return Math.round((typeof window !== "undefined" ? window.innerHeight : 800) * 0.6);
}

function clampHeight(px: number): number {
  return Math.max(MIN_TASK_HEIGHT, Math.min(px, maxTaskHeight()));
}

function readBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

function readNum(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function readSet(key: string): Set<string> {
  try {
    const v = localStorage.getItem(key);
    if (!v) return new Set();
    const arr = JSON.parse(v);
    return new Set(Array.isArray(arr) ? (arr as string[]) : []);
  } catch {
    return new Set();
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — non-fatal, state still lives in memory */
  }
}

export function ShellProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readBool(PERSIST.sidebar, false),
  );
  const [taskPanelCollapsed, setTaskPanelCollapsed] = useState(() =>
    readBool(PERSIST.taskCollapsed, false),
  );
  const [taskPanelHeight, setTaskPanelHeightState] = useState(() =>
    clampHeight(readNum(PERSIST.taskHeight, DEFAULT_TASK_HEIGHT)),
  );
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    readSet(PERSIST.expanded),
  );
  const [selectedTask, setSelectedTask] = useState<SelectedTask | null>(null);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      write(PERSIST.sidebar, next ? "1" : "0");
      return next;
    });
  }, []);

  const toggleTaskPanel = useCallback(() => {
    setTaskPanelCollapsed((prev) => {
      const next = !prev;
      write(PERSIST.taskCollapsed, next ? "1" : "0");
      return next;
    });
  }, []);

  // Debounce the localStorage write so a pointer drag (fires per pixel)
  // doesn't hammer storage; the in-memory value updates synchronously.
  const heightWriteTimer = useRef<number | null>(null);
  const setTaskPanelHeight = useCallback((px: number) => {
    const clamped = clampHeight(px);
    setTaskPanelHeightState(clamped);
    if (heightWriteTimer.current != null) {
      window.clearTimeout(heightWriteTimer.current);
    }
    heightWriteTimer.current = window.setTimeout(() => {
      write(PERSIST.taskHeight, String(clamped));
    }, 200);
  }, []);

  const toggleTenant = useCallback((tenantId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      write(PERSIST.expanded, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const isTenantExpanded = useCallback(
    (tenantId: string) => expanded.has(tenantId),
    [expanded],
  );

  const openTask = useCallback((task: SelectedTask) => setSelectedTask(task), []);
  const closeTask = useCallback(() => setSelectedTask(null), []);

  const value = useMemo<ShellContextValue>(
    () => ({
      sidebarCollapsed,
      toggleSidebar,
      taskPanelCollapsed,
      toggleTaskPanel,
      taskPanelHeight,
      setTaskPanelHeight,
      isTenantExpanded,
      toggleTenant,
      selectedTask,
      openTask,
      closeTask,
    }),
    [
      sidebarCollapsed,
      toggleSidebar,
      taskPanelCollapsed,
      toggleTaskPanel,
      taskPanelHeight,
      setTaskPanelHeight,
      isTenantExpanded,
      toggleTenant,
      selectedTask,
      openTask,
      closeTask,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}
